import { todayLocal } from '@/lib/dates'
import { useState } from 'react'
import { Download, History, Plus, Upload } from 'lucide-react'
import {
  ConfirmDialog, EmptyState, ErrorState, FormDialog, ImportDialog, PageHeader,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { downloadFile, toApiProblem } from '@/lib/api/client'
import { formProblem } from '@/lib/api/fieldErrors'
import { bulkPaths } from '@/lib/bulk'
import { usePayElementMutations, usePayElementVersions, usePayElements } from '../api'
import { CALCULATION_TYPE_LABELS, ELEMENT_TYPE_LABELS } from '../types'
import type { CalculationType, ElementType, PayElementView } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

interface ElementDraft {
  id?: string
  code: string
  name: string
  elementType: ElementType
  evaluationOrder: string
  prorateOnJoinExit: boolean
  prorateOnUnpaidLeave: boolean
  arrearsCapable: boolean
  taxable: boolean
  statutory: boolean
  wpsIncluded: boolean
  affectsGratuity: boolean
  affectsGpssa: boolean
  currency: string
  glAccountCode: string
  active: boolean
}

const EMPTY: ElementDraft = {
  code: '', name: '', elementType: 'EARNING', evaluationOrder: '100',
  prorateOnJoinExit: true, prorateOnUnpaidLeave: false, arrearsCapable: false,
  taxable: false, statutory: false, wpsIncluded: true, affectsGratuity: false,
  affectsGpssa: false, currency: 'AED', glAccountCode: '', active: true,
}

/**
 * What the company pays, and what each element has been worth.
 *
 * <p>An element is stable; its rule is versioned. That is what makes reopening a closed month
 * meaningful — it recomputes what the rules said then rather than applying this year's rates
 * to last year's payroll.
 */
export function PayElementsPage() {
  useBreadcrumb()
  const elements = usePayElements()
  const { create, update, addVersion, retire } = usePayElementMutations()

  const [draft, setDraft] = useState<ElementDraft | null>(null)
  const [versionsFor, setVersionsFor] = useState<PayElementView | null>(null)
  const [newVersion, setNewVersion] = useState<{
    effectiveFrom: string; calculationType: CalculationType; fixedAmount: string
    percentage: string; percentageOfCode: string; formula: string
  } | null>(null)
  const [retiring, setRetiring] = useState<PayElementView | null>(null)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [codeError, setCodeError] = useState<string | null>(null)

  const versions = usePayElementVersions(versionsFor?.id)

  async function onSave() {
    if (!draft) return
    setError(null)
    setCodeError(null)
    const body = {
      code: draft.code.trim(),
      name: draft.name,
      elementType: draft.elementType,
      evaluationOrder: Number(draft.evaluationOrder) || 100,
      prorateOnJoinExit: draft.prorateOnJoinExit,
      prorateOnUnpaidLeave: draft.prorateOnUnpaidLeave,
      arrearsCapable: draft.arrearsCapable,
      taxable: draft.taxable,
      statutory: draft.statutory,
      wpsIncluded: draft.wpsIncluded,
      affectsGratuity: draft.affectsGratuity,
      affectsGpssa: draft.affectsGpssa,
      currency: draft.currency || null,
      glAccountCode: draft.glAccountCode || null,
      active: draft.active,
    }
    try {
      if (draft.id) await update.mutateAsync({ id: draft.id, body })
      else await create.mutateAsync(body)
      setDraft(null)
    } catch (caught) {
      // A refused code change says why — paid in a run, read by a formula, already taken —
      // and the reason belongs under the box that was changed, not in a banner.
      const problem = formProblem(caught, 'That element could not be saved')
      const onCode = problem.fields.code
      setCodeError(onCode ?? null)
      setError(onCode ? 'The code cannot be changed — see the message under it.' : problem.summary)
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Pay elements"
        subtitle="What the company pays. Changing what one is worth adds a version rather than editing history."
        actions={
          <>
          <Button
            type="button" variant="outline"
            onClick={() => {
              void downloadFile(
                bulkPaths('/v1/payroll/masters/elements').export, 'pay-elements.xlsx')
            }}
          >
            <Download className="size-4" aria-hidden="true" />
            Export
          </Button>
          <Button type="button" variant="outline" onClick={() => setImporting(true)}>
            <Upload className="size-4" aria-hidden="true" />
            Import
          </Button>
          <Button type="button" onClick={() => { setError(null); setCodeError(null); setDraft({ ...EMPTY }) }}>
            <Plus className="size-4" aria-hidden="true" />
            New element
          </Button>
          </>
        }
      />

      <ImportDialog
        open={importing}
        onOpenChange={setImporting}
        what="pay elements"
        prefix="/v1/payroll/masters/elements"
        invalidateKey={['payroll']}
      />

      <ConfirmDialog
        open={retiring !== null}
        onOpenChange={(open) => { if (!open) setRetiring(null) }}
        title={`Retire ${retiring?.name ?? 'this element'}?`}
        description="It stops being offered on new packages. Every payslip that used it keeps naming it, so a figure somebody was paid can still be explained — this is never a delete."
        confirmLabel="Retire it"
        loading={retire.isPending}
        error={error}
        onConfirm={() => {
          if (!retiring) return
          setError(null)
          retire.mutate(retiring.id, {
            onSuccess: () => setRetiring(null),
            onError: (caught) => setError(
              toApiProblem(caught)?.detail ?? 'That element could not be retired.'),
          })
        }}
      />

      <Card>
        {elements.isPending && <Skeleton className="m-4 h-24" />}
        {elements.isError && (
          <ErrorState error={elements.error} onRetry={() => void elements.refetch()} />
        )}
        {elements.isSuccess && elements.data.length === 0 && (
          <EmptyState
            title="Nothing is defined"
            description="Until an element exists, a payroll run calculates nothing at all."
          />
        )}
        {elements.isSuccess && elements.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Code</th>
                  <th className="p-3 text-start font-medium">Name</th>
                  <th className="p-3 text-start font-medium">Kind</th>
                  <th className="p-3 text-start font-medium">Order</th>
                  <th className="p-3 text-start font-medium">Flags</th>
                  <th className="p-3 text-start font-medium">GL</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {elements.data.map((element) => (
                  <tr key={element.id} className={`border-b border-slate-100 ${element.active ? '' : 'text-slate-400'}`}>
                    <td className="p-3 font-mono">{element.code}</td>
                    <td className="p-3">{element.name}</td>
                    <td className="p-3 text-slate-600">
                      {ELEMENT_TYPE_LABELS[element.elementType as ElementType] ?? element.elementType}
                    </td>
                    <td className="p-3">{element.evaluationOrder}</td>
                    <td className="p-3 text-xs text-slate-600">
                      {[
                        element.affectsGratuity && 'gratuity basis',
                        element.affectsGpssa && 'GPSSA',
                        element.wpsIncluded && 'WPS',
                        element.taxable && 'taxable',
                        element.statutory && 'statutory',
                        element.arrearsCapable && 'arrears',
                      ].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td className="p-3 font-mono text-xs">
                      {element.glAccountCode ?? <span className="text-amber-700">unmapped</span>}
                    </td>
                    <td className="p-3 text-end">
                      <span className="flex justify-end gap-1">
                        <Button
                          type="button" variant="outline" size="sm"
                          onClick={() => setVersionsFor(element)}
                        >
                          <History className="size-4" aria-hidden="true" />
                          Versions
                        </Button>
                        <Button
                          type="button" variant="outline" size="sm"
                          onClick={() => {
                            setError(null)
                            setCodeError(null)
                            setDraft({
                              id: element.id, code: element.code.trim(), name: element.name,
                              elementType: element.elementType as ElementType,
                              evaluationOrder: String(element.evaluationOrder),
                              prorateOnJoinExit: element.prorateOnJoinExit,
                              prorateOnUnpaidLeave: element.prorateOnUnpaidLeave,
                              arrearsCapable: element.arrearsCapable, taxable: element.taxable,
                              statutory: element.statutory, wpsIncluded: element.wpsIncluded,
                              affectsGratuity: element.affectsGratuity,
                              affectsGpssa: element.affectsGpssa,
                              currency: element.currency ?? '',
                              glAccountCode: element.glAccountCode ?? '',
                              active: element.active,
                            })
                          }}
                        >
                          Edit
                        </Button>
                        {element.active && (
                          <Button
                            type="button" variant="outline" size="sm"
                            onClick={() => { setError(null); setRetiring(element) }}
                          >
                            Retire
                          </Button>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setCodeError(null) } }}
        size="lg"
        title={draft?.id ? 'Edit pay element' : 'New pay element'}
        description="Flags are properties of the element rather than of any one run."
        submitLabel={draft?.id ? 'Save changes' : 'Create it'}
        loading={create.isPending || update.isPending}
        error={error}
        onSubmit={() => void onSave()}
      >
        {draft && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="element-code">Code</Label>
                <Input
                  id="element-code"
                  className="font-mono"
                  value={draft.code}
                  aria-invalid={codeError ? true : undefined}
                  aria-describedby={codeError ? 'element-code-error' : undefined}
                  onChange={(event) =>
                    setDraft({ ...draft, code: event.target.value.toUpperCase() })}
                />
                {codeError ? (
                  <p id="element-code-error" className="mt-1 text-xs text-[var(--color-danger)]">
                    {codeError}
                  </p>
                ) : draft.id && (
                  // Changeable until something holds on to it: the server refuses once the
                  // element has been paid in a run or another formula reads it, and says which.
                  <p className="mt-1 text-xs text-slate-600">
                    Formulas refer to elements by code, so it can change only before the first
                    payroll and while no formula reads it.
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="element-name">Name</Label>
                <Input
                  id="element-name"
                  value={draft.name}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="element-type">Kind</Label>
                <Select
                  id="element-type"
                  className="w-full"
                  value={draft.elementType}
                  onChange={(event) =>
                    setDraft({ ...draft, elementType: event.target.value as ElementType })}
                >
                  {Object.entries(ELEMENT_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="element-order">Evaluation order</Label>
                <Input
                  id="element-order"
                  type="number"
                  value={draft.evaluationOrder}
                  onChange={(event) => setDraft({ ...draft, evaluationOrder: event.target.value })}
                />
                <p className="mt-1 text-xs text-slate-600">
                  Lower first. An element reading another must come after it.
                </p>
              </div>
              <div>
                <Label htmlFor="element-gl">GL account</Label>
                <Input
                  id="element-gl"
                  value={draft.glAccountCode}
                  placeholder="Left blank posts as unmapped"
                  onChange={(event) => setDraft({ ...draft, glAccountCode: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="element-currency">Currency</Label>
                <Input
                  id="element-currency"
                  maxLength={3}
                  value={draft.currency}
                  onChange={(event) =>
                    setDraft({ ...draft, currency: event.target.value.toUpperCase() })}
                />
              </div>
            </div>

            <fieldset className="grid gap-1 sm:grid-cols-2">
              <legend className="mb-1 text-sm font-medium">Flags</legend>
              {([
                ['affectsGratuity', 'Part of the basic wage gratuity accrues on'],
                ['affectsGpssa', 'Part of the GPSSA contributory wage'],
                ['wpsIncluded', 'Included in the WPS file'],
                ['prorateOnJoinExit', 'Prorate when somebody joins or leaves mid-month'],
                ['prorateOnUnpaidLeave', 'Prorate for unpaid leave'],
                ['arrearsCapable', 'Can be paid as arrears'],
                ['taxable', 'Taxable'],
                ['statutory', 'Statutory'],
                ['active', 'Active'],
              ] as const).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="size-4"
                    checked={draft[key]}
                    onChange={(event) => setDraft({ ...draft, [key]: event.target.checked })}
                  />
                  {label}
                </label>
              ))}
            </fieldset>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={versionsFor != null}
        onOpenChange={(open) => { if (!open) { setVersionsFor(null); setNewVersion(null) } }}
        size="lg"
        title={versionsFor ? `What ${versionsFor.code} has been worth` : ''}
        description="A period is calculated with the version in force for that period."
        submitLabel={newVersion ? 'Add the version' : 'Close'}
        cancelLabel="Done"
        error={error}
        onSubmit={() => {
          if (!newVersion || !versionsFor) { setVersionsFor(null); return }
          void (async () => {
            setError(null)
            try {
              await addVersion.mutateAsync({
                id: versionsFor.id,
                body: {
                  effectiveFrom: newVersion.effectiveFrom,
                  calculationType: newVersion.calculationType,
                  fixedAmount: newVersion.fixedAmount ? Number(newVersion.fixedAmount) : null,
                  percentage: newVersion.percentage ? Number(newVersion.percentage) : null,
                  percentageOfCode: newVersion.percentageOfCode || null,
                  formula: newVersion.formula || null,
                },
              })
              setNewVersion(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That version could not be added')
            }
          })()
        }}
      >
        <div className="space-y-3">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-slate-600">
              <tr>
                <th className="p-2 text-start font-medium">From</th>
                <th className="p-2 text-start font-medium">To</th>
                <th className="p-2 text-start font-medium">How</th>
                <th className="p-2 text-end font-medium">Value</th>
              </tr>
            </thead>
            <tbody>
              {versions.data?.map((version) => (
                <tr key={version.id} className="border-b border-slate-100">
                  <td className="p-2">{version.effectiveFrom}</td>
                  <td className="p-2">{version.effectiveTo ?? 'open'}</td>
                  <td className="p-2 text-slate-600">
                    {CALCULATION_TYPE_LABELS[version.calculationType as CalculationType]
                      ?? version.calculationType}
                  </td>
                  <td className="p-2 text-end">
                    {version.fixedAmount ?? version.percentage ?? version.formula ?? '—'}
                  </td>
                </tr>
              ))}
              {versions.isSuccess && versions.data.length === 0 && (
                <tr><td colSpan={4} className="p-2 text-slate-600">
                  No version yet — this element produces nothing until one exists.
                </td></tr>
              )}
            </tbody>
          </table>

          {!newVersion ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => setNewVersion({
                effectiveFrom: todayLocal(),
                calculationType: 'FIXED', fixedAmount: '', percentage: '',
                percentageOfCode: '', formula: '',
              })}
            >
              <Plus className="size-4" aria-hidden="true" />
              Change what it is worth
            </Button>
          ) : (
            <div className="space-y-3 rounded-md border border-slate-200 p-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="version-from">Effective from</Label>
                  <DatePicker
                    id="version-from"
                    value={newVersion.effectiveFrom}
                    onChange={(event) =>
                      setNewVersion({ ...newVersion, effectiveFrom: event.target.value })}
                  />
                  <p className="mt-1 text-xs text-slate-600">
                    The open version is closed the day before this.
                  </p>
                </div>
                <div>
                  <Label htmlFor="version-type">How it is worked out</Label>
                  <Select
                    id="version-type"
                    className="w-full"
                    value={newVersion.calculationType}
                    onChange={(event) => setNewVersion({
                      ...newVersion, calculationType: event.target.value as CalculationType })}
                  >
                    {Object.entries(CALCULATION_TYPE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                </div>
              </div>
              {newVersion.calculationType === 'FIXED' && (
                <div>
                  <Label htmlFor="version-fixed">Amount</Label>
                  <Input
                    id="version-fixed" type="number" value={newVersion.fixedAmount}
                    onChange={(event) =>
                      setNewVersion({ ...newVersion, fixedAmount: event.target.value })}
                  />
                </div>
              )}
              {newVersion.calculationType === 'PERCENTAGE' && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="version-pct">Percentage</Label>
                    <Input
                      id="version-pct" type="number" value={newVersion.percentage}
                      onChange={(event) =>
                        setNewVersion({ ...newVersion, percentage: event.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="version-of">Of which element</Label>
                    <Input
                      id="version-of" className="font-mono" value={newVersion.percentageOfCode}
                      placeholder="BASIC"
                      onChange={(event) => setNewVersion({
                        ...newVersion, percentageOfCode: event.target.value.toUpperCase() })}
                    />
                  </div>
                </div>
              )}
              {newVersion.calculationType === 'FORMULA' && (
                <div>
                  <Label htmlFor="version-formula">Formula</Label>
                  <Input
                    id="version-formula" className="font-mono" value={newVersion.formula}
                    placeholder="min(BASIC * 0.5, 5000)"
                    onChange={(event) =>
                      setNewVersion({ ...newVersion, formula: event.target.value })}
                  />
                  <p className="mt-1 text-xs text-slate-600">
                    Arithmetic over other element codes only. Anything else is refused.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </FormDialog>
    </div>
  )
}
